import React from "react";
import { useTranslation } from "react-i18next";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TagSelect from "components/basic/tagSelect";
import { Attendee, AttendeeOption, LeadCustomer } from "../../types";

interface AttendeeInputProps {
  value: Attendee[];
  onChange: (attendees: Attendee[]) => void;
  suggestions: AttendeeOption[];
  optional?: boolean;
  label?: string;
  placeholder?: string;
  // Called when a suggestion carrying an address is newly added.
  onLeadPicked?: (address: string) => void;
  // Lead customers keyed by email — used to show a "view quotes" info icon.
  leadsByEmail?: Map<string, LeadCustomer>;
  onViewQuotes?: (email: string) => void;
}

const emailOf = (item: string | AttendeeOption): string =>
  typeof item === "string" ? item.trim() : item.email;

const AttendeeInput: React.FC<AttendeeInputProps> = ({
  value,
  onChange,
  suggestions,
  optional = false,
  label,
  placeholder,
  onLeadPicked,
  leadsByEmail,
  onViewQuotes,
}) => {
  const { t } = useTranslation("calendar");
  return (
    <TagSelect<AttendeeOption>
      options={suggestions}
      value={value}
      label={label}
      placeholder={placeholder ?? t("booking.attendee_placeholder")}
      getKey={(option) => option.email}
      getOptionLabel={(option) =>
        option.name ? `${option.name} (${option.email})` : option.email
      }
      getChipLabel={(option) => option.name || option.email}
      renderChipIcon={(option) => {
        const hasQuotes =
          (leadsByEmail?.get(option.email)?.quotes.length ?? 0) > 0;
        if (!hasQuotes) {
          return undefined;
        }
        return (
          <InfoOutlinedIcon
            titleAccess={t("booking.view_quotes")}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onViewQuotes?.(option.email);
            }}
            sx={{ cursor: "pointer", "&:hover": { opacity: 0.7 } }}
          />
        );
      }}
      onChange={(items) => {
        const prevEmails = new Set(value.map((a) => a.email));
        const normalized: Attendee[] = [];
        const seen = new Set<string>();

        items.forEach((item) => {
          const email = emailOf(item);
          if (!email || seen.has(email)) {
            return;
          }
          seen.add(email);

          if (typeof item === "string") {
            normalized.push({ email, optional });
          } else {
            normalized.push({ email, name: item.name, optional });
            if (!prevEmails.has(email) && item.address) {
              onLeadPicked?.(item.address);
            }
          }
        });

        onChange(normalized);
      }}
    />
  );
};

export default AttendeeInput;
